/**
 * A card's question, answered where it is asked (the phone's and the workbench's Home do the same): one button per
 * answer, a menu when there are more than fit, a short field and Send for an answer in the person's own words, and a
 * reason asked for first when a refusal carries one. It sends through the Inbox's own calls (`actNotification`,
 * `answerNotification`), so the audit trail and the refusals are the Inbox's. A card that asks cannot be cleared; the
 * feed model already keeps it on Home until it is answered.
 */
import { Ellipsis } from '@gravity-ui/icons';
import { Button, Dropdown, Label } from '@heroui/react';
import { useState } from 'react';

import { ApiError, routes } from '../../api/client.ts';
import type { FeedItem } from '../../sharedHome.js';
import { StatusMark } from '../ui.tsx';
import { isStale, type RecordAct, recordActs, refusalWords, splitActs, splitRecordId } from './feedAskModel.js';

const FIELD = 'min-w-0 flex-1 rounded-lg border border-field-border bg-field-background px-3 py-1.5 text-sm outline-none focus:border-accent';

export function FeedAsk({ item, onAnswered }: { item: FeedItem; onAnswered: () => void }) {
  const acts = recordActs(item);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  // An answer waiting on the person's words or reason: which one, and what they have typed.
  const [asking, setAsking] = useState<RecordAct>();
  const [text, setText] = useState('');
  const { row, more } = splitActs(acts);
  const because = item.ask?.openBecause;

  const send = async (a: RecordAct, typed?: string) => {
    const rec = splitRecordId(a.recordId);
    if (!rec) return setError('That question can no longer be answered here. Open it instead.');
    setBusy(a.actionId);
    setError(undefined);
    try {
      if (a.words) await routes.answerNotification(rec.projectId, rec.id, a.actionId, (typed ?? '').trim());
      else await routes.actNotification(rec.projectId, rec.id, a.actionId, a.reason ? typed?.trim() : undefined);
      setAsking(undefined);
      setText('');
      // Answered: the record closes, so Home is asked again and the card settles by itself.
      onAnswered();
    } catch (e) {
      setError(e instanceof ApiError ? refusalWords(e.message, e.body, e.code) : (e as Error).message);
      // A card that is out of date (answered elsewhere) settles on the next read; the words stay until it does.
      if (e instanceof ApiError && isStale(e.code)) window.setTimeout(onAnswered, 1500);
    } finally {
      setBusy(undefined);
    }
  };
  const choose = (a: RecordAct) => {
    setError(undefined);
    if (a.words || a.reason) {
      setAsking(a);
      setText('');
    } else void send(a);
  };

  if (acts.length === 0 && !because) return null;
  return (
    <div className="home-holo__ask" data-feed-ask role="group" aria-label="Answer this card">
      {error ? (
        <div role="alert" data-feed-ask-error className="home-holo__ask-error">
          <StatusMark state="error" label={error} wrap />
        </div>
      ) : null}
      {asking ? (
        <div className="home-holo__ask-row">
          <input
            autoFocus
            aria-label={asking.words ? 'Your answer' : 'Why? (optional)'}
            className={FIELD}
            value={text}
            maxLength={2000}
            placeholder={asking.words ? 'Your answer' : 'Why? (optional)'}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (asking.reason || text.trim()) && !busy) void send(asking, text);
              if (e.key === 'Escape') setAsking(undefined);
            }}
          />
          <Button size="sm" variant={asking.reason ? 'danger-soft' : 'primary'} isDisabled={Boolean(busy) || (Boolean(asking.words) && !text.trim())} isPending={Boolean(busy)} onPress={() => void send(asking, text)}>
            {asking.words ? 'Send' : asking.label.replace(/…$/, '')}
          </Button>
          <Button size="sm" variant="ghost" isDisabled={Boolean(busy)} onPress={() => setAsking(undefined)}>
            Not now
          </Button>
        </div>
      ) : acts.length > 0 ? (
        <div className="home-holo__ask-row">
          {row.map((a, i) => (
            <Button key={a.actionId} size="sm" variant={i === 0 ? 'primary' : a.reason ? 'danger-soft' : 'secondary'} isDisabled={Boolean(busy)} isPending={busy === a.actionId} onPress={() => choose(a)} data-feed-answer={a.actionId}>
              {a.label}
            </Button>
          ))}
          {more.length > 0 ? (
            <Dropdown>
              <Button size="sm" variant="secondary" isDisabled={Boolean(busy)} aria-label="More answers" data-feed-answer="more">
                <Ellipsis className="size-4" />
                More
              </Button>
              <Dropdown.Popover placement="top end" className="min-w-44">
                <Dropdown.Menu
                  aria-label="More answers"
                  onAction={(k) => {
                    const a = more.find((m) => m.actionId === String(k));
                    if (a) choose(a);
                  }}
                >
                  {more.map((a) => (
                    <Dropdown.Item key={a.actionId} id={a.actionId} textValue={a.label}>
                      <Label>{a.label}</Label>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          ) : null}
        </div>
      ) : (
        <p className="home-holo__ask-note">{because}</p>
      )}
    </div>
  );
}
